<script lang="ts">
  import z, { ZodError } from 'zod';

  import type { FormFieldError } from '$lib/utils/definitions';
  import { formatFormErrors } from '$lib/utils/helpers';

  import Wrapper from '$components/common/Simulator/Wrapper.svelte';
  import Description from '$components/common/Simulator/Description.svelte';
  import Form from '$components/common/Simulator/Form.svelte';
  import Input from '$components/common/Input.svelte';
  import Actions from '$components/common/Simulator/Actions.svelte';
  import Action from '$components/common/Simulator/Action.svelte';
  import { formatEuro } from '$lib/utils/formatters';

  import acquisitionSimulatorManager from '$lib/managers/acquisition-simulator.svelte';

  let errors: FormFieldError = $state({});

  const FormData = z.object({
    notaryFees: z
      .number()
      .gte(0, 'Veuillez saisir un chiffre supérieur à 0.')
      .optional(),
    oneTimeExpenses: z
      .number()
      .gte(0, 'Veuillez saisir un chiffre supérieur à 0.')
      .optional(),
  });

  let {
    currentStep,
    steps,
    housingType,
    notaryFees,
    oneTimeExpenses,
    estimatedNotaryFees,
    loading,
    acquisitionSimulation,
    nextStep,
    previousStep,
    updateAcquisitionSimulation,
    goToPreviousStep,
  } = $derived(acquisitionSimulatorManager);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    const payload = {
      notaryFees: notaryFees || estimatedNotaryFees,
      oneTimeExpenses: acquisitionSimulatorManager.oneTimeExpenses || 0,
    };

    try {
      FormData.parse(payload);
      errors = {};

      if (acquisitionSimulation) {
        await updateAcquisitionSimulation(payload);
      }
    } catch (e) {
      errors = formatFormErrors((e as ZodError).issues);
    }
  };
</script>

<Wrapper>
  <Description
    title="Simulateur d'acquisition"
    stepTitle={`${currentStep.step}. ${currentStep.title}`}
    nextStepTitle={nextStep?.title}
    currentStep={currentStep.step}
    stepCount={steps.length}>
    <p>
      Précisez les différents frais liés à l'achat du logement : frais de
      notaire ou autres frais ponctuels. Si certains montants sont inconnus, le
      simulateur peut les estimer automatiquement. Cette section vous permet de
      ne pas avoir de mauvaise surprise avec des frais non anticipés.
    </p>
  </Description>

  <Form onSubmit={handleSubmit}>
    <div class="fieldset-container">
      <fieldset class="fr-fieldset">
        <div class="fr-fieldset__element fr-mb-4w">
          <Input
            value={notaryFees}
            label="Frais de notaire (€)"
            labelTooltip={`
              <div>
                Frais obligatoires lors de l'achat, couvrant taxes et
                rémunération du notaire.
                <ul>
                  <li>Pour un achat neuf: 2,3% du prix du logement</li>
                  <li>Pour de l'ancien: 7,8%.</li>
                </ul>
              </div>
            `}
            type="number"
            id="notary-fees"
            hint="Laissez le champs vide pour estimation automatique."
            error={errors.notaryFees}
            forceNoMarginBottom
            placeholder="Exemple: 10 000€"
            onChange={(e) => {
              const { value } = e.target as HTMLInputElement;

              if (value === '') {
                acquisitionSimulatorManager.notaryFees = undefined;
              } else {
                acquisitionSimulatorManager.notaryFees = Number(value);
              }
            }} />
          {#if !notaryFees || notaryFees < 0}
            <span class="fr-text--sm">
              Estimation
              <b>
                {formatEuro(estimatedNotaryFees)}
              </b>
              <b>({housingType === 'new' ? '2,3%' : '7,8%'})</b>
              du prix du logement)
            </span>
          {/if}
        </div>

        <div class="fr-fieldset__element">
          <Input
            value={oneTimeExpenses}
            label="Frais ponctuels (€)"
            labelTooltip="Coût du déménagement, ouverture des compteurs, frais d'agence immobilière, etc. Hors frais de garantie et de prêt."
            type="number"
            id="one-time-expenses"
            hint="Laissez le champs vide ou saisissez 0 si vous n'avez pas de frais ponctuels."
            error={errors.oneTimeExpenses}
            forceNoMarginBottom
            placeholder="Exemple: 7 500€"
            onChange={(e) => {
              const { value } = e.target as HTMLInputElement;

              if (value === '') {
                acquisitionSimulatorManager.oneTimeExpenses = undefined;
              } else {
                acquisitionSimulatorManager.oneTimeExpenses = Number(value);
              }
            }} />
        </div>
      </fieldset>
    </div>

    <Actions>
      <Action
        direction="previous"
        label={previousStep?.title as string}
        onClick={goToPreviousStep}
        {loading} />
      <Action
        direction="next"
        label={nextStep?.title as string}
        type="submit"
        {loading} />
    </Actions>
  </Form>
</Wrapper>
